<template>
  <v-row no-gutters :class="{ 'text--disabled': $attrs.disabled }" justify="space-between">
    <v-col class="leftCol">
      <v-row no-gutters class="pb-2">
        <v-col cols="12">
          <v-row no-gutters>
            <v-col cols="auto" class="text-left">
              <span class="font-weight-medium">
                {{ $i18n.t(leftTitle) }}
              </span>
            </v-col>
            <template v-if="!hideNewGroupButton">
              <v-spacer></v-spacer>
              <v-col cols="auto">
                <base-button
                  :disabled="disabled"
                  @click="$emit('addGroup')"
                  translation-path="button.addGroup"
                  class="mr-0"
                ></base-button>
              </v-col>
            </template>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters>
        <v-col cols="12">
          <draggable-lists-nested
            v-bind="$attrs"
            :is-layers="isLayers"
            :disable-multi-drag="disableMultiDrag"
            :child-prop-name="childPropName"
            :items="getLeftList()"
            :level="1"
            v-on="$listeners"
            :disabled="disabled"
            :layersDotsItems="layersDotsItems"
          >
            <template #listElement="el">
              <slot name="listElement" v-bind="el" v-on="el.listeners"> </slot>
            </template>
          </draggable-lists-nested>
        </v-col>
      </v-row>
    </v-col>
    <v-col class="rightCol">
      <v-row no-gutters class="pb-2">
        <v-col cols="12">
          <v-row no-gutters class="align-center" style="gap: 0.5rem">
            <v-col cols="auto" class="text-left pr-2">
              <span class="font-weight-medium">
                {{ $i18n.t(rightTitle) }}
              </span>
            </v-col>
            <v-col cols="auto" class="text-left ml-auto" style="justify-self: center">
              <slot name="button" v-bind="{ disabled }"> </slot>
            </v-col>
            <v-col cols="auto" class="text-left ml-auto">
              <slot name="search" v-bind="{ disabled }"> </slot>
            </v-col>
          </v-row>
        </v-col>
      </v-row>
      <v-row no-gutters class="fill-height">
        <v-col cols="12">
          <draggable
            id="list--right"
            class="fill-height"
            tag="ul"
            :multi-drag="!disableMultiDrag"
            ghost-class="draggable-selected"
            selected-class="draggable-selected"
            v-bind="$attrs"
            v-model="computedRightList"
            :group="rightListGroup"
            @change="onChange"
            :scrollSensitivity="200"
            :forceFallback="true"
            handle=".handleDrag"
          >
            <li class="list-group-item" v-for="element in computedRightList" :key="element.id" :id="element.id">
              <slot name="rightListElement" v-bind="element"> </slot>
            </li>
          </draggable>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import { get } from 'vuex-pathify';

import draggable from 'vuedraggable-multi';
import DraggableListsNested from '@/components/DraggableListsNested';

export default {
  name: 'DraggableLists',
  components: {
    draggable,
    DraggableListsNested,
  },
  props: {
    isLayers: {
      type: Boolean,
      default: true,
    },
    hasAddButton: {
      type: Boolean,
      default: true,
    },
    leftTitle: {
      type: String,
      required: true,
    },
    rightTitle: {
      type: String,
      required: true,
    },
    leftList: {
      type: Array,
      required: true,
    },
    rightList: {
      type: Array,
      required: true,
    },
    layersDotsItems: {
      type: Array,
      required: false,
    },
    childPropName: {
      type: String,
      default: 'layers',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    hideNewGroupButton: {
      type: Boolean,
      default: false,
    },
    disableMultiDrag: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    layers: get('layers/layers'),
    groupPrefix() {
      return this.isLayers ? 'layers' : 'attributes';
    },
    rightListGroup() {
      return `${this.groupPrefix}_2`;
    },
    hasAnyRequiredOnRightSide() {
      return this.rightList.some(element => element.required);
    },
    computedRightList: {
      get() {
        return this.rightList.map(element => {
          return { ...element, role: this.layers[element.id]?.role };
        });
      },
      set(nV) {
        this.$emit('update:rightList', nV);
      },
    },
  },
  watch: {
    hasAnyRequiredOnRightSide(nV) {
      this.$emit('update:hasAnyRequiredOnRightSide', nV);
    },
  },
  methods: {
    onChange(e) {
      if (e.added) {
        this.$emit('removedFromGroups', [...[e.added].flat()]);
      }
    },
    getLeftList() {
      return this.leftList.map(element => {
        return {
          ...element,
          ...(element.layers
            ? {
                layers: element.layers.map(layer => {
                  return { ...layer, role: this.layers[layer.id]?.role };
                }),
              }
            : {}),
        };
      });
    },
  },
};
</script>

<style scoped lang="scss">
.rightCol {
  @include customColSize(48);
}
.leftCol {
  @include customColSize(48);
}
ul {
  padding: 0px;
  list-style: none;
}
::v-deep .draggable-selected .element {
  @include draggableSelectedElement();
}
</style>
